import { type FC, type ReactNode } from 'react'
import { Col, Form, InputGroup, Row } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { DEFAULT_TIMEZONE } from '../../../../../components/TimezoneSelector/constants'
import { IntegrationServiceId } from '../../../../../common/integrations'
import {
  type AccountSchedulingSettings,
  AvailabilityMode,
  type AvailabilitySchedule,
  type Location,
  LocationKind,
  MeetingMode,
  type SchedulingEventType,
} from '../../../../../models/Scheduling'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../../store/hooks'
import { useGetMeQuery } from '../../../../../store/user/user'
import { DefaultAvailabilityEditor } from '../../../../Scheduling/shared/DefaultAvailabilityEditor'
import { buildEventTypeLocationOptions, eventTypeLocationPatch, eventTypeLocationSelectValue } from '../../../../Scheduling/shared/eventTypeOptions'
import { useDefaultLocationPreviews } from '../../../../Scheduling/shared/useDefaultLocationPreviews'
import { formatTimezoneLabel } from '../../../../Scheduling/shared/schedulingDisplay'
import { resolveLocationPreviewId } from '../../../../Scheduling/flows/form/scheduleHelpers'

const BUFFER_OPTIONS = [0, 5, 10, 15, 30]
const NOTICE_OPTIONS = [0, 4, 12, 24, 48, 72, 120, 168]
const WINDOW_OPTIONS = [14, 30, 60, 90, 120]

const bufferLabel = (minutes: number): string => (minutes === 0 ? 'None' : `${minutes} minutes`)
const noticeLabel = (hours: number): string => {
  if (hours === 0) {
    return 'None'
  }
  if (hours < 24) {
    return hours === 1 ? '1 hour' : `${hours} hours`
  }
  if (hours % 168 === 0) {
    const weeks = hours / 168
    return weeks === 1 ? '1 week' : `${weeks} weeks`
  }
  const days = hours / 24
  return days === 1 ? '1 day' : `${days} days`
}
const windowLabel = (days: number): string => `${days} days`

const WEEKLY_HOURS_OPTIONS = [
  { mode: AvailabilityMode.ACCOUNT_DEFAULT, label: 'Use account default' },
  { mode: AvailabilityMode.USER_DEFAULT, label: 'Use each user’s default' },
  { mode: AvailabilityMode.CUSTOM, label: 'Custom weekly hours' },
]

const VIDEO_PLATFORMS = [IntegrationServiceId.ZOOM, IntegrationServiceId.TEAMS, IntegrationServiceId.MEET]

interface AvailabilityTabProps {
  draft: SchedulingEventType
  accountSettings?: AccountSchedulingSettings
  locations: Location[]
  disabled: boolean
  onDraftChange: (patch: Partial<SchedulingEventType>) => void
}

interface SectionHeaderProps {
  children: string
}

const SectionHeader: FC<SectionHeaderProps> = ({ children }) => <div className='lead-small zocks-text-gray-500 text-uppercase'>{children}</div>

interface FieldLabelProps {
  label: string
  helper: string
}

const FieldLabel: FC<FieldLabelProps> = ({ label, helper }) => (
  <>
    <div className='bold'>{label}</div>
    <div className='paragraph-small zocks-text-gray-600'>{helper}</div>
  </>
)

const PROFILE_SETTINGS_PATH = '/account/profile-settings/profile-settings'

interface ZoneInput {
  draft: SchedulingEventType
  location?: Location
}

const readsLocationZone = (draft: SchedulingEventType): boolean =>
  draft.meetingMode === MeetingMode.IN_PERSON && draft.availabilityMode !== AvailabilityMode.USER_DEFAULT

const sharedLocationTimeZone = ({ draft, location }: ZoneInput): string | undefined =>
  readsLocationZone(draft) && draft.location?.kind !== LocationKind.USER_DEFAULT ? (location?.timeZone ?? undefined) : undefined

interface TimeZoneValueProps {
  helper: string
  label: string
  source: string
  children: ReactNode
}

const TimeZoneValue: FC<TimeZoneValueProps> = ({ helper, label, source, children }) => (
  <div className='d-flex flex-column gap-2'>
    <div className='d-flex flex-column'>
      <FieldLabel label='Time zone' helper={helper} />
    </div>
    <InputGroup>
      <Form.Control readOnly aria-label='Time zone' value={label} className='border-end-0 zocks-text-gray-500 zocks-bg-color-white' />
      <InputGroup.Text className='zocks-gap-0.5 paragraph-small zocks-bg-color-white'>
        <i className='bi bi-lock' aria-hidden='true' />
        {source}
      </InputGroup.Text>
    </InputGroup>
    <span className='paragraph-small zocks-text-gray-700'>{children}</span>
  </div>
)

interface TimeZoneFieldProps extends ZoneInput {
  viewerTimeZone: string
  viewerLocationTimeZone?: string
}

const timeZoneValue = ({ draft, location, viewerTimeZone, viewerLocationTimeZone }: TimeZoneFieldProps): TimeZoneValueProps => {
  if (readsLocationZone(draft) && draft.location?.kind === LocationKind.USER_DEFAULT && viewerLocationTimeZone != null) {
    return {
      helper: 'Weekly hours follow each user’s default location time zone.',
      label: formatTimezoneLabel(viewerLocationTimeZone),
      source: 'Set by location',
      children: 'Each user sees their own default location’s time zone here. To change it, change your default location.',
    }
  }
  const locationTimeZone = sharedLocationTimeZone({ draft, location })
  if (locationTimeZone != null) {
    return {
      helper: 'Weekly hours are interpreted in this time zone.',
      label: formatTimezoneLabel(locationTimeZone),
      source: 'Set by location',
      children: 'Comes from the event location. To change it, update the location above.',
    }
  }
  return {
    helper: 'Weekly hours are interpreted in this time zone.',
    label: formatTimezoneLabel(viewerTimeZone),
    source: 'Your time zone',
    children: (
      <>
        Each user’s hours follow their own time zone. To change yours, go to <NavLink to={PROFILE_SETTINGS_PATH}>Profile settings.</NavLink>
      </>
    ),
  }
}

const TimeZoneField: FC<TimeZoneFieldProps> = (props) => <TimeZoneValue {...timeZoneValue(props)} />

interface HostHoursNoticeProps {
  usedByCount: number
}

const HostHoursNotice: FC<HostHoursNoticeProps> = ({ usedByCount }) => (
  <div className='d-flex flex-column p-3 rounded-2 zocks-bg-color-gray-100'>
    <span className='paragraph-small bold'>Each host’s own weekly hours</span>
    <span className='paragraph-small zocks-text-gray-600'>
      Clients see the days and times each host set in their own Availability settings, in that host’s time zone. Hosts without their own hours use the account
      default. {usedByCount === 1 ? '1 user has' : `${usedByCount} users have`} this event type on their booking page.
    </span>
  </div>
)

export const AvailabilityTab: FC<AvailabilityTabProps> = ({ draft, accountSettings, locations, disabled, onDraftChange }) => {
  const isInPerson = draft.meetingMode === MeetingMode.IN_PERSON
  const accountId = useAppSelector(selectAccountId)
  const { data: me } = useGetMeQuery({ accountId })
  const viewerTimeZone = me?.effectiveSettings?.timezone || DEFAULT_TIMEZONE

  const { accountDefaultLocation, userDefaultLocation } = useDefaultLocationPreviews(locations)
  const locationOptions = buildEventTypeLocationOptions(locations, accountDefaultLocation, userDefaultLocation, draft)
  const resolvedLocationId = resolveLocationPreviewId(draft.location, accountDefaultLocation?.id, userDefaultLocation?.id)
  const resolvedLocation: Location | undefined = resolvedLocationId
    ? [...locations, ...(userDefaultLocation ? [userDefaultLocation] : [])].find((location) => location.id === resolvedLocationId)
    : undefined

  const locationTimeZone = sharedLocationTimeZone({ draft, location: resolvedLocation })
  const customSchedule = draft.availabilityMode === AvailabilityMode.CUSTOM ? (draft.availabilitySchedule ?? null) : null
  const accountDefaultAvailability: AvailabilitySchedule = accountSettings?.defaults.availabilitySchedule ?? { days: [] }

  return (
    <div className='d-flex flex-column gap-4'>
      <div className='d-flex flex-column gap-3'>
        <SectionHeader>Where</SectionHeader>
        <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <FieldLabel label='Location' helper={`This event type is set as ${isInPerson ? 'an in-person' : 'a virtual'} event. Change it in the sidebar.`} />
          </div>
          {isInPerson && locationOptions.length > 0 ? (
            <Form.Select
              className='w-auto'
              aria-label='Location'
              value={eventTypeLocationSelectValue(draft)}
              disabled={disabled}
              onChange={(event) => onDraftChange(eventTypeLocationPatch(event.currentTarget.value))}
            >
              <option value='' disabled>
                Select a location
              </option>
              {locationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          ) : null}
        </div>
        {isInPerson && locationOptions.length === 0 ? (
          <div className='d-flex align-items-start zocks-gap-0.5 p-3 rounded-2 zocks-bg-color-warning-background paragraph-small'>
            <i className='bi bi-exclamation-triangle zocks-text-gray-600 mt-1' />
            <span>Add a location under Availability before you can save this in-person event type.</span>
          </div>
        ) : isInPerson ? (
          <div className='d-flex flex-column gap-2 p-3 rounded-2 zocks-bg-color-gray-100'>
            <span className='d-flex align-items-center zocks-gap-0.5 paragraph-small'>
              <i className='bi bi-geo-alt zocks-text-gray-600' />
              {resolvedLocation?.address ?? 'No location set'}
            </span>
            {resolvedLocation ? (
              <span className='d-flex align-items-center zocks-gap-0.5 paragraph-small'>
                <i className='bi bi-building zocks-text-gray-600' />
                {resolvedLocation.name}
              </span>
            ) : null}
            {locationTimeZone != null ? (
              <span className='d-flex align-items-center zocks-gap-0.5 paragraph-small'>
                <i className='bi bi-globe zocks-text-gray-600' />
                {formatTimezoneLabel(locationTimeZone)}
              </span>
            ) : null}
          </div>
        ) : (
          <div className='d-flex align-items-start justify-content-between gap-3 p-3 rounded-2 zocks-bg-color-gray-100'>
            <div className='d-flex align-items-start zocks-gap-0.5'>
              <i className='bi bi-info-circle zocks-text-gray-600 mt-1' />
              <div className='d-flex flex-column'>
                <span className='paragraph-small'>Each user sets their own video platform. Zocks adds the call link when a meeting is scheduled.</span>
                <span className='paragraph-small zocks-text-gray-600'>
                  Works with Zoom, Teams and Google Meet. Manage under{' '}
                  <NavLink to='/account/integration-settings' className='paragraph-small'>
                    Integrations
                  </NavLink>
                  .
                </span>
              </div>
            </div>
            <div className='d-flex align-items-center zocks-gap-0.5 flex-shrink-0'>
              {VIDEO_PLATFORMS.map((serviceId) => (
                <IntegrationIcon key={serviceId} serviceId={serviceId} width='1.25rem' height='1.25rem' />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className='d-flex flex-column gap-3'>
        <SectionHeader>When</SectionHeader>
        <div className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <FieldLabel label='Weekly hours' helper='Set the days and times clients can book this event type.' />
          </div>
          <Form.Select
            className='w-auto'
            aria-label='Weekly hours mode'
            value={draft.availabilityMode}
            disabled={disabled}
            onChange={(event) => {
              const mode = WEEKLY_HOURS_OPTIONS.find((option) => option.mode === event.currentTarget.value)?.mode ?? AvailabilityMode.ACCOUNT_DEFAULT
              onDraftChange({ availabilityMode: mode, availabilitySchedule: mode === AvailabilityMode.CUSTOM ? accountDefaultAvailability : undefined })
            }}
          >
            {WEEKLY_HOURS_OPTIONS.map((option) => (
              <option key={option.mode} value={option.mode}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </div>
        {draft.availabilityMode === AvailabilityMode.USER_DEFAULT ? (
          <HostHoursNotice usedByCount={draft.usedByCount} />
        ) : (
          <div className='p-3 rounded-2 zocks-bg-color-gray-100'>
            <DefaultAvailabilityEditor
              availability={customSchedule ?? accountDefaultAvailability}
              readOnly={disabled || !customSchedule}
              onAvailabilityChange={(availabilitySchedule) => onDraftChange({ availabilitySchedule })}
            />
          </div>
        )}
        <TimeZoneField
          draft={draft}
          location={resolvedLocation}
          viewerTimeZone={viewerTimeZone}
          viewerLocationTimeZone={userDefaultLocation?.timeZone ?? undefined}
        />
      </div>

      <Row className='gy-3'>
        <Col md={6}>
          <FieldLabel label='Buffer before' helper='Time blocked before each booking.' />
          <Form.Select
            value={draft.bufferBeforeMinutes ?? 0}
            disabled={disabled}
            aria-label='Buffer before'
            onChange={(event) => onDraftChange({ bufferBeforeMinutes: Number(event.currentTarget.value) })}
          >
            {BUFFER_OPTIONS.map((minutes) => (
              <option key={minutes} value={minutes}>
                {bufferLabel(minutes)}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={6}>
          <FieldLabel label='Buffer after' helper='Time blocked after each booking.' />
          <Form.Select
            value={draft.bufferAfterMinutes ?? 0}
            disabled={disabled}
            aria-label='Buffer after'
            onChange={(event) => onDraftChange({ bufferAfterMinutes: Number(event.currentTarget.value) })}
          >
            {BUFFER_OPTIONS.map((minutes) => (
              <option key={minutes} value={minutes}>
                {bufferLabel(minutes)}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={6}>
          <FieldLabel label='Minimum advance notice' helper='How far ahead clients must book.' />
          <Form.Select
            value={(draft.minimumNoticeMinutes ?? 0) / 60}
            disabled={disabled}
            aria-label='Minimum advance notice'
            onChange={(event) => onDraftChange({ minimumNoticeMinutes: Number(event.currentTarget.value) * 60 })}
          >
            {NOTICE_OPTIONS.map((hours) => (
              <option key={hours} value={hours}>
                {noticeLabel(hours)}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={6}>
          <FieldLabel label='Booking window' helper='How far in the future clients can book.' />
          <Form.Select
            value={draft.bookingWindowDays ?? 0}
            disabled={disabled}
            aria-label='Booking window'
            onChange={(event) => onDraftChange({ bookingWindowDays: Number(event.currentTarget.value) })}
          >
            {WINDOW_OPTIONS.map((days) => (
              <option key={days} value={days}>
                {windowLabel(days)}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>
    </div>
  )
}
